import { ButtonSmall } from "@src/Components/Button"
import {
  Content,
  Divider,
  FormElement,
  Heading,
  LinkWrapper,
  Panel,
  PanelContainer,
  SubHeading,
} from "@src/Components/Common"
import FloatingLabelInput from "@src/Components/FloatingLabelInput"
import { Link } from "@src/Components/Link"
import PasswordInput from "@src/Components/PasswordInput"
import { Section } from "@src/Components/StyledUtils"
import C from "@src/Controller"
import { useLocale } from "@src/Hooks/useLocale"
import { tState } from "@src/Model/Model"
import MessageContainer from "@src/Portal/MessageContainer"
import { inviteLinkLookupWatcher, inviteLinkSignupWatcher } from "@src/Sagas"
import * as T from "@src/types"
import * as AmazonCognitoIdentity from "amazon-cognito-identity-js"
import { toLower } from "lodash"
import * as React from "react"
import ReCAPTCHA from "react-google-recaptcha"
import { useDispatch, useSelector } from "react-redux"

import { FooterIcon } from "./FooterIcon"

interface ISignUp {
  height: number
  width: number
  subscriptionId: string
  recaptchaRef: any
  onSignUp: (
    subscriptionId: string,
    username: string,
    password: string,
    callback: ({
      success,
      error,
    }: {
      success: boolean
      error?: Error
      result: AmazonCognitoIdentity.ISignUpResult
    }) => void,
  ) => void
  redirectToLogin: () => void
  executeCaptcha: () => void
  verifyToken: (token: any, callback: any) => void
  resetCaptcha: () => void
  uiRules?: T.tChannelUiRules
}

const SignUp = ({
  height,
  subscriptionId,
  recaptchaRef,
  onSignUp,
  redirectToLogin,
  executeCaptcha,
  verifyToken,
  resetCaptcha,
  uiRules,
}: ISignUp) => {
  const dispatch = useDispatch()
  const { currentLocale } = useLocale()

  const [email, setEmail] = React.useState("")
  const [password, setPassword] = React.useState("")
  const [passwordValid, setPasswordValid] = React.useState(false)
  const [confirmPassword, setConfirmPassword] = React.useState("")
  const [confirmPasswordValid, setConfirmPasswordValid] = React.useState(false)

  const inviteLinkLookup = useSelector(
    (state: tState) => state.account.inviteLinkLookup,
  )
  const inviteLinkSignup = useSelector(
    (state: tState) => state.account.inviteLinkSignup,
  )

  const pendingSignUpAfterLookupRef = React.useRef(false)

  const clearState = () => {
    setEmail("")
    setPassword("")
  }

  const goToLoginScreen = React.useCallback(() => {
    clearState()
    redirectToLogin()
  }, [redirectToLogin])

  const submitSignUp = React.useCallback(() => {
    if (email.trim().length === 0 || password.trim().length === 0) {
      C.toastNotification(
        "Form Incomplete. Please fill empty fields.",
        "danger",
      )

      return resetCaptcha()
    }

    pendingSignUpAfterLookupRef.current = true
    dispatch(
      inviteLinkLookupWatcher(encodeURIComponent(email.trim().toLowerCase())),
    )
  }, [email, password, dispatch, resetCaptcha])

  React.useEffect(() => {
    if (!pendingSignUpAfterLookupRef.current) {
      return
    }

    if (inviteLinkLookup.isLoading) {
      return
    }

    pendingSignUpAfterLookupRef.current = false

    const linkId = inviteLinkLookup.data?.linkId

    if (linkId) {
      C.heapIdentify(email.trim().toLowerCase())
      dispatch(
        inviteLinkSignupWatcher({
          linkId,
          password: password.trim(),
          locale: currentLocale,
        }),
      )

      return
    }

    onSignUp(subscriptionId, toLower(email.trim()), password.trim(), (ret) => {
      if (ret.success) {
        return
      }

      if (ret.error.name === "UsernameExistsException") {
        C.toastNotification(
          <div>
            Looks like you already have an account.{" "}
            <span
              style={{
                color: "#fffff",
                textDecoration: "underline",
                cursor: "pointer",
              }}
              onClick={goToLoginScreen}
            >
              Sign in now
            </span>
          </div>,
          "danger",
          20000,
        )
      } else {
        C.toastNotification(ret.error.message || ret.error.toString(), "danger")
      }
    })
  }, [
    inviteLinkLookup.isLoading,
    inviteLinkLookup.error,
    inviteLinkLookup.data,
    dispatch,
    email,
    password,
    currentLocale,
    resetCaptcha,
    onSignUp,
    subscriptionId,
    goToLoginScreen,
  ])

  const onEmailChange = (event: any) => {
    const value = event.target.value
    setEmail(value)
  }

  const onPasswordChange = (event: any) => {
    const value = event.target.value
    setPassword(value)
    setConfirmPassword("")
    setConfirmPasswordValid(false)
  }

  const onConfirmPasswordChange = (event: any) => {
    const value = event.target.value
    setConfirmPassword(value)
  }

  const onChange = (value: any) => {
    if (value !== null) {
      verifyToken(value, (response: any) => {
        if (response.success) {
          submitSignUp()
        } else {
          C.toastNotification("You smell like a robot.", "danger")
        }
      })
    } else {
      console.log("CAPTCHA ERROR")
    }
  }

  const pContent = { height }

  const onSubmit = React.useCallback(() => {
    setEmail(email.trim())
    executeCaptcha()
  }, [email, executeCaptcha])

  const isSubmitting = inviteLinkLookup.isLoading || inviteLinkSignup.isLoading

  return (
    <Content {...pContent}>
      <MessageContainer />
      <PanelContainer height={height}>
        <Panel>
          <Section flex="center center column">
            <Heading align="center" margin="20px 0px">
              Create your free account.
            </Heading>
          </Section>
          <Section flex="center center column">
            <SubHeading>
              Simplify monitoring, training, and compliance for your
              <br />
              organization with Cyber Safety.
            </SubHeading>
            {uiRules?.isTrialDisabled ? null : (
              <FormElement flexDirection="row" marginBottom="10px">
                <FooterIcon margin text="Free 30 day trial" />
                <FooterIcon margin text="No credit card" />
                <FooterIcon text="Cancel any time" />
              </FormElement>
            )}
            <FormElement>
              <FloatingLabelInput
                id="signup-email"
                label="Your work email address"
                type="text"
                value={email}
                onChange={onEmailChange}
              />
              <PasswordInput
                id="signup-password"
                label="Create password"
                value={password}
                onChange={onPasswordChange}
                isValid={(valid: boolean) => setPasswordValid(valid)}
              />
              <PasswordInput
                id="signup-confirm-password"
                label="Confirm password"
                passwordToMatch={password}
                value={confirmPassword}
                onChange={onConfirmPasswordChange}
                isValid={(valid: boolean) => setConfirmPasswordValid(valid)}
                noValidation
              />
              <Section flex="center center column" w="100%">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey="6Lfi57UUAAAAACcMby1mAPrHTNZVGG_B2t_7KDDN"
                  size="invisible"
                  onChange={onChange}
                />
              </Section>
              <Section w="100%" margin="30 0 0 0">
                <ButtonSmall
                  onClick={onSubmit}
                  id="CyberSafety_Trial_Lead_Button"
                  disabled={
                    !(password && passwordValid && confirmPasswordValid) ||
                    isSubmitting
                  }
                >
                  {uiRules?.isTrialDisabled
                    ? "Create Account"
                    : "Start Free Trial"}
                </ButtonSmall>
              </Section>
              <LinkWrapper>
                <p>
                  Already have an account? <br />
                  <Link onClick={goToLoginScreen}>Sign in</Link>
                </p>
              </LinkWrapper>
              <Divider />
              <LinkWrapper fontWeight="400">
                <p>
                  By continuing, you agree to Cyber Safety&nbsp;
                  <Link
                    isUnderline
                    onClick={() =>
                      window.open(
                        "https://www.munichre.com/hsb/en/general/legal/terms-of-use.html",
                      )
                    }
                  >
                    Terms&nbsp;of&nbsp;Service
                  </Link>
                  &nbsp;and
                  <br />
                  <Link
                    isUnderline
                    onClick={() =>
                      window.open(
                        "https://www.munichre.com/hsb/en/general/legal/privacy-statement.html",
                      )
                    }
                  >
                    Privacy&nbsp;Policy
                  </Link>
                  .
                </p>
              </LinkWrapper>
            </FormElement>
          </Section>
        </Panel>
      </PanelContainer>
    </Content>
  )
}

export default SignUp
